import { useEffect, useMemo, useState } from 'react';
import css from './index.module.scss';
import { BreadCrumb, Button, Chip, Drawer, IconNode, Loader, Tab, Tabs } from '@components/base';
import { CLIENT_ROUTES } from '@router/routes';
import { useParams, useSearchParams } from 'react-router-dom';
import NonConsolidationDetail from './components/non-consolidation-detail';
import ConsolidationDetail from './components/consolidation-detail';
import { CURRENCY, ENQUIRY_TYPE, MARKET_TYPE, WEIGHT_SYSTEM } from '@helpers/constants';
import { fetchQuoteProductList, getEnquiryDetails } from '@services/quote.service';
import notify from '@helpers/toastify-helper';
import GeneratePDFTab from './components/generate-pdf-tab';
import AccessWrapper from '@authorization/access-wrapper';
import GeneratePDF from './components/generate-pdf';
import Images from '@assets/images';
import EnquiryEdit from './components/enquiry-edit';
import { DeletePrompt } from '@components/common';
import QuoteBasicDetail from './components/quote-basic-detail';
import QuoteActivity from './components/activity-tab';
import { ICurrency, IQuoteDetail } from '@helpers/types/quotes';

const QUOTE_STATE_MAPPING = [
  {
    value: 0,
    key: 'product',
    default: true
  },
  {
    value: 1,
    key: 'documents'
  },
  {
    value: 2,
    key: 'activity'
  }
];

const QuoteDetail = (props: any) => {
  const { actions } = props.modulePermissions;
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('activeTab');
  const initialTab = useMemo(() => {
    return (
      QUOTE_STATE_MAPPING.find((tab) => tab.key === activeTabParam)?.value ||
      QUOTE_STATE_MAPPING.find((tab) => tab.default)?.value ||
      0
    );
  }, [activeTabParam]);
  const [quoteDetail, setQuoteDetail] = useState<IQuoteDetail>({
    isLoading: false,
    activeTab: initialTab,
    openViewQuoteModal: false,
    detailModalData: null,
    isConsolidation: false,
    data: null,
    productData: null,
    openAddEditQuoteModal: false,
    addEditQuoteModalData: null,
    addEditQuoteIsEdit: false,
    addEditQuoteIsNew: false,
    selectedCurrency: [],
    selectedWeightSystemChildOptions: [],
    currency1: null,
    currency2: null,
    fxRate: null,
    openGeneratePDFModal: false,
    openEditEnquiryDrawer: false,
    openEditEnquiryData: null,
    readableEnquiryNumber: '',
    isFcl: false,
    isMtt: false,
    selectedQuotes: [],
    showDisclaimer: false,
    primaryUnit: null,
    isAddProduct: false,
    productOptions: [],
    enquiryType: ENQUIRY_TYPE.INTERNATIONAL
  });

  const [showQuotes, setShowQuotes] = useState<{ [key: number]: boolean }>({});

  const {
    activeTab,
    isConsolidation,
    openGeneratePDFModal,
    openEditEnquiryDrawer,
    data,
    productData,
    readableEnquiryNumber,
    selectedQuotes,
    currency1,
    currency2,
    showDisclaimer,
    openEditEnquiryData,
    enquiryType
  } = quoteDetail;

  const filterProductOptions = async (event: any, param?: any) => {
    if (param) {
      const response = await fetchQuoteProductList({ product_name: param });
      if (response?.success) {
        setQuoteDetail((prevState: IQuoteDetail) => ({
          ...prevState,
          productOptions: response?.data?.results ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    } else {
      const response = await fetchQuoteProductList();
      if (response?.success) {
        setQuoteDetail((prevState: IQuoteDetail) => ({
          ...prevState,
          productOptions: response?.data?.results ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    }
  };

  const fetchEnquiryDetail = async (enquiry_id?: string) => {
    setQuoteDetail((prevState: IQuoteDetail) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await getEnquiryDetails(enquiry_id ? enquiry_id : params.id ?? '');
    if (response?.success) {
      const { data } = response;
      let productDetails: any;
      if (data.is_consolidation) {
        productDetails = data.consolidations;
      } else {
        const groupedData: any = {};
        data.quotes.forEach((quote: any) => {
          const { product_details, ...rest } = quote;
          const key = product_details.product_id;

          if (!groupedData[key]) {
            groupedData[key] = {
              product: product_details,
              details: []
            };
          }
          groupedData[key].details.push(rest);
        });
        productDetails = Object.values(groupedData);
      }
      setQuoteDetail((prevState: IQuoteDetail) => {
        const currencyOne = CURRENCY.find((item) => item.value === data.currency_type_one) || null;
        const currencyTwo = CURRENCY.find((item) => item.value === data.currency_type_two) || null;

        return {
          ...prevState,
          data: response.data,
          productData: productDetails,
          isConsolidation: data.is_consolidation,
          isFcl: data.container_type == 'FCL',
          isMtt: data.sourcing_type == 'MTT',
          currency1: currencyOne,
          currency2: currencyTwo,
          fxRate: data.fx_rate,
          selectedWeightSystemChildOptions:
            WEIGHT_SYSTEM.find((item) => item.value === data.weight_system)?.childOptions || [],
          selectedCurrency: [currencyOne, currencyTwo].filter(
            (cur): cur is ICurrency => cur !== null
          ),
          readableEnquiryNumber: data.readable_enquiry_number,
          primaryUnit:
            WEIGHT_SYSTEM.find((item) => item.value === data?.weight_system)?.childOptions?.find(
              (item) => item.value === data.primary_unit
            ) ?? null,
          enquiryType:
            data.market_type === MARKET_TYPE.INTERNATIONAL
              ? ENQUIRY_TYPE.INTERNATIONAL
              : ENQUIRY_TYPE.DOMESTIC,
          isLoading: false
        };
      });
    } else if (response?.error) {
      notify({
        severity: 'error',
        message: 'Something Went Wrong' // response?.error?.message
      });
      setQuoteDetail((prevState: IQuoteDetail) => ({
        ...prevState,
        isLoading: false
      }));
    }
  };

  useEffect(() => {
    fetchEnquiryDetail(params.id ?? '');
  }, [params.id]);

  useEffect(() => {
    setQuoteDetail((prevState: IQuoteDetail) => ({
      ...prevState,
      activeTab: initialTab
    }));
  }, [activeTabParam]);

  const handleTabChange = (tab: number) => {
    setQuoteDetail((prevState: IQuoteDetail) => ({
      ...prevState,
      activeTab: tab
    }));
    const activeTabKey = QUOTE_STATE_MAPPING.find((item) => item.value === tab)?.key || 'product';
    setSearchParams({
      ...Object.fromEntries(searchParams),
      activeTab: activeTabKey
    });
  };

  const handleGeneratePDFClick = () => {
    if (selectedQuotes.length === 0) {
      if (productData?.length > 0) {
        const allExpanded = productData.reduce((acc: any, _item: any, index: number) => {
          acc[index] = true;
          return acc;
        }, {});
        setShowQuotes(allExpanded);
      }
      notify({
        severity: 'warning',
        message: 'Please select at least one quote to generate PDF'
      });
      return;
    }
    setQuoteDetail((prevState: IQuoteDetail) => ({
      ...prevState,
      openGeneratePDFModal: true
    }));
  };

  return (
    <AccessWrapper show={actions?.read}>
      <main className={css.mainWrapper}>
        <BreadCrumb>
          <BreadCrumb.Item value={0} to={`/${CLIENT_ROUTES.quoteGenerator}`}>
            Enquiry List
          </BreadCrumb.Item>
          <BreadCrumb.Item
            value={1}
            to={`/${CLIENT_ROUTES.quoteGenerator}/${CLIENT_ROUTES.detail}/${params.id}`}
          >
            {readableEnquiryNumber}
          </BreadCrumb.Item>
        </BreadCrumb>

        <div className={css.container}>
          <section className={css.basicDetails}>
            <div className={css.header}>
              <div className={css.headerText}>
                Basic Details
                <div className={css.headerChips}>
                  <Chip
                    variant={
                      data?.market_type === MARKET_TYPE.DOMESTIC ? 'filled-green' : 'filled-blue'
                    }
                    label={
                      data?.market_type === MARKET_TYPE.DOMESTIC ? 'Domestic' : 'International'
                    }
                    className={css.marketType}
                  />
                  <Chip className={css.chips} variant="filled-blue" label={data?.container_type} />
                  <Chip
                    className={css.chips}
                    variant="filled-yellow"
                    label={data?.sourcing_type == 'MTT' ? 'MTT' : 'Non-MTT'}
                  />
                  <Chip
                    className={css.chips}
                    variant="filled-green"
                    label={data?.is_consolidation ? 'Consolidation' : 'Non-Consolidation'}
                  />
                </div>
              </div>
              <Button
                variant="contained"
                className={css.headerButton}
                onClick={() => {
                  setQuoteDetail((prevState: IQuoteDetail) => {
                    return {
                      ...prevState,
                      openEditEnquiryDrawer: true,
                      openEditEnquiryData: data
                    };
                  });
                }}
              >
                <div>
                  <IconNode src={Images.editWhite} alt="quote icon" className={css.editIcon} />
                </div>
                <div className={css.headerButtonText}>Edit</div>
              </Button>
            </div>
            <QuoteBasicDetail
              data={data}
              quoteDetail={{
                currency1,
                currency2,
                readableEnquiryNumber,
                enquiryType: enquiryType
              }}
            />
          </section>

          <section className={css.productDetails}>
            <div className={css.header}>
              <Tabs value={activeTab}>
                <Tab value={0} label="Products" onClick={() => handleTabChange(0)} />
                <Tab value={1} label="Quote sheets" onClick={() => handleTabChange(1)} />
                <Tab value={2} label="Activity" onClick={() => handleTabChange(2)} />
              </Tabs>
            </div>

            <AccessWrapper show={actions?.create}>
              {activeTab === 0 && (
                <div className={css.enquiryButtonsWrapper}>
                  <div className={css.enquiryButtons}>
                    <Button variant="outlined-secondary" onClick={handleGeneratePDFClick}>
                      Generate PDF
                    </Button>
                  </div>
                  <div className={css.enquiryButtons}>
                    <Button
                      variant="outlined-secondary"
                      onClick={() => {
                        setQuoteDetail((prevState: IQuoteDetail) => {
                          return {
                            ...prevState,
                            openAddEditQuoteModal: true,
                            addEditQuoteModalData: null,
                            addEditQuoteIsEdit: false,
                            addEditQuoteIsNew: false,
                            isAddProduct: true
                          };
                        });
                      }}
                    >
                      Add Product
                    </Button>
                  </div>
                </div>
              )}
            </AccessWrapper>

            {activeTab === 0 &&
              (isConsolidation ? (
                <ConsolidationDetail
                  productData={productData}
                  setQuoteDetail={setQuoteDetail}
                  quoteDetail={quoteDetail}
                  fetchEnquiryDetail={fetchEnquiryDetail}
                  showQuotes={showQuotes}
                  setShowQuotes={setShowQuotes}
                  actions={actions}
                />
              ) : (
                <NonConsolidationDetail
                  productData={productData}
                  setQuoteDetail={setQuoteDetail}
                  quoteDetail={quoteDetail}
                  fetchEnquiryDetail={fetchEnquiryDetail}
                  showQuotes={showQuotes}
                  setShowQuotes={setShowQuotes}
                  actions={actions}
                  filterProductOptions={filterProductOptions}
                />
              ))}
            {activeTab === 1 && <GeneratePDFTab />}
            {activeTab === 2 && <QuoteActivity />}
          </section>
        </div>

        {openGeneratePDFModal && (
          <GeneratePDF
            open={openGeneratePDFModal}
            onClose={() =>
              setQuoteDetail((prevState: IQuoteDetail) => ({
                ...prevState,
                openGeneratePDFModal: false
              }))
            }
            isConsolidation={isConsolidation}
            date={data?.last_valid_date ? new Date(data?.last_valid_date) : null}
            onSubmitClick={() => {
              fetchEnquiryDetail(params.id ?? '');
              setQuoteDetail((prevState: IQuoteDetail) => ({
                ...prevState,
                openGeneratePDFModal: false,
                selectedQuotes: [],
                isLoading: false
              }));
            }}
            selectedQuotes={selectedQuotes}
            enquiryId={params.id ?? ''}
            setQuoteDetail={setQuoteDetail}
            marketType={data?.market_type}
          />
        )}

        <AccessWrapper show={actions?.create}>
          {showDisclaimer && (
            <DeletePrompt
              open={showDisclaimer}
              onClose={() =>
                setQuoteDetail((prevState: IQuoteDetail) => ({
                  ...prevState,
                  showDisclaimer: false
                }))
              }
              onConfirm={() =>
                setQuoteDetail((prevState: IQuoteDetail) => ({
                  ...prevState,
                  showDisclaimer: false,
                  openEditEnquiryDrawer: false,
                  openAddEditQuoteModal: false,
                  addEditQuoteIsNew: false,
                  addEditQuoteIsEdit: false
                }))
              }
              message="Are you sure you want to close?"
            />
          )}
        </AccessWrapper>

        {openEditEnquiryDrawer && (
          <EnquiryEdit
            open={openEditEnquiryDrawer}
            onClose={() =>
              setQuoteDetail((prevState: IQuoteDetail) => ({
                ...prevState,
                showDisclaimer: true
              }))
            }
            onSubmitClose={() => {
              fetchEnquiryDetail(params.id);
              setQuoteDetail((prevState: IQuoteDetail) => ({
                ...prevState,
                showDisclaimer: false,
                openEditEnquiryDrawer: false,
                openAddEditQuoteModal: false,
                addEditQuoteIsNew: false,
                addEditQuoteIsEdit: false
              }));
            }}
            data={openEditEnquiryData}
            enquiryType={enquiryType}
          />
        )}
        <Loader open={quoteDetail.isLoading} />
      </main>
    </AccessWrapper>
  );
};

export default QuoteDetail;
